'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { UserCircle, Package, Award, LogOut } from 'lucide-react';

import PartnerHeader from '@/components/PartnerHeader';
import ReputationBadge from '@/components/ReputationBadge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SearchBar } from '@/components/ui/search';
import { useAuthStore, useProductStore, useRepScoreStore } from '@/presentation/hooks/useStores';

import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import RepScoreDrawer from '../drawers/RepScoreDrawer';
import MobileSideDrawer from './MobileSideDrawer';

// Paths where Header's mobile search should be hidden
// (these pages have their own search in PageNavigationBar or don't need search)
const MOBILE_SEARCH_EXCLUDED_PATHS = [
  '/checkout',
  '/profile',
  '/products',  // Has PageNavigationBar withSearch
  '/order',     // Transaction pages - no search needed
];

const Header = observer(() => {
  const router = useRouter();
  const pathname = usePathname();
  const authStore = useAuthStore();
  const productStore = useProductStore();
  const repScoreStore = useRepScoreStore();
  const menuItemList = productStore.categoryNavigation;

  // State for mobile side drawer
  const [isMobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isRepScoreDrawerOpen, setRepScoreDrawerOpen] = useState(false);

  // Load navigation categories from API
  useEffect(() => {
    const loadNavigationCategories = async () => {
      try {
        await productStore.loadCategoryNavigation();
        // Filter categories that should appear in the featured menu
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load navigation categories:', error);
      }
    };

    loadNavigationCategories();
  }, [productStore]);

  // Load rep score when user is authenticated
  useEffect(() => {
    if (!authStore.isHydrated) return;
    if (authStore.isAuthenticated && authStore.userId) {
      repScoreStore.loadUserRepScore(authStore.userId).catch(() => undefined);
    }
  }, [authStore.isHydrated, authStore.isAuthenticated, authStore.userId, repScoreStore]);

  const handleOpenRepScoreDrawer = () => {
    setRepScoreDrawerOpen(true);
  };

  // Generate initials from user name (like Google/Gmail)
  const getUserInitials = (): string => {
    const firstName = authStore.currentUser?.first_name || '';
    const lastName = authStore.currentUser?.last_name || '';

    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  const signUserOut = () => {
    void authStore.signOut();
    router.replace('/logout');
  };

  // Check if mobile search bar should be shown
  // Hidden on pages that have their own PageNavigationBar search or don't need search
  const shouldShowMobileSearch = () => {
    if (!pathname) return true; // Safe default - show search if pathname not available during SSR
    return !MOBILE_SEARCH_EXCLUDED_PATHS.some(path => pathname.startsWith(path));
  };

  return (
    <div className="bg-white">
      {/* Mobile Side Drawer */}
      <MobileSideDrawer
        open={isMobileDrawerOpen}
        onOpenChange={setMobileDrawerOpen}
      />

      {/* Rep Score Drawer */}
      <RepScoreDrawer
        open={isRepScoreDrawerOpen}
        onOpenChange={setRepScoreDrawerOpen}
      />

      <header className="container py-5">
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileDrawerOpen(true)}
              className="block sm:hidden"
              aria-label="Open menu"
            >
              <Image
                src="/assets/svgs/Menu.svg"
                alt="Menu"
                width={24}
                height={24}
              />
            </button>
            <Image
              onClick={() => router.replace('/')}
              src="/assets/svgs/motito.svg"
              alt="Logo"
              width={89}
              height={24}
              className="min-h-6 cursor-pointer"
            />
          </div>
          {!pathname.includes('/checkout') && !pathname.includes('/profile') && (
            <SearchBar className="hidden flex-auto gap-3 md:flex" />
          )}

          {!authStore.isHydrated ? (
            <div className="flex items-center justify-center gap-2">
              <Skeleton className="h-10 w-20 rounded-full" />
              <Skeleton className="h-10 w-20 rounded-full" />
            </div>
          ) : !authStore.isAuthenticated ? (
            <div className="flex gap-2">
              <Button
                size="lg"
                className="hidden rounded-full bg-motitoBlue md:block"
                onClick={() => router.push('/sign-up')}
              >
                Sign Up
              </Button>
              <Button
                variant="outline"
                size="lg"
                className=" rounded-full"
                onClick={() => router.push('/sign-in')}
              >
                Log In
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              {/* Reputation Badge */}
              <ReputationBadge
                onClick={handleOpenRepScoreDrawer}
                className="hidden sm:flex"
              />

              <Popover>
                <PopoverTrigger>
                  <Image
                    src="/assets/svgs/Icons/Notification bell.svg"
                    width={38}
                    height={38}
                    alt="notification"
                    className="m-0"
                  />
                </PopoverTrigger>
                <PopoverContent>
                  <p>User Notification</p>
                </PopoverContent>
              </Popover>

              <p className="hidden sm:block">Hi, {authStore.userDisplayName}</p>
              <Link href="/profile">
                <Avatar className="size-9">
                  <AvatarImage src={authStore.photoUrl} />
                  <AvatarFallback className="bg-motitoBlue text-white text-sm font-semibold">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
              </Link>

              <Popover
                onOpenChange={(open) => {
                  if (!open) return;
                  router.prefetch('/profile');
                  router.prefetch('/order');
                }}
              >
                <PopoverTrigger>
                  <Image
                    src="/assets/svgs/Icons/CaretDown.svg"
                    width={16}
                    height={16}
                    alt="caret"
                  />
                </PopoverTrigger>
                <PopoverContent className="flex w-56 flex-col gap-1 p-2">
                  {/* User info header */}
                  <div className="px-2 py-2">
                    <p className="font-medium text-gray-900">
                      {authStore.userFullDisplayName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {authStore.userEmail || authStore.userPhoneNumber || ''}
                    </p>
                  </div>

                  <div className="my-1 h-px bg-gray-200" />

                  <Link href="/profile" className="w-full">
                    <Button className="w-full justify-start gap-2" variant="ghost">
                      <UserCircle className="size-4" />
                      Profile
                    </Button>
                  </Link>

                  <Link href="/order" className="w-full">
                    <Button className="w-full justify-start gap-2" variant="ghost">
                      <Package className="size-4" />
                      Orders
                    </Button>
                  </Link>

                  <Button
                    className="w-full justify-start gap-2"
                    variant="ghost"
                    onClick={handleOpenRepScoreDrawer}
                  >
                    <Award className="size-4" />
                    Rep Score
                  </Button>

                  <div className="my-1 h-px bg-gray-200" />

                  <Button
                    onClick={signUserOut}
                    variant="ghost"
                    className="w-full justify-start gap-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    <LogOut className="size-4" />
                    Logout
                  </Button>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
        {shouldShowMobileSearch() && (
          <SearchBar className="mt-3 flex gap-3 md:hidden" />
        )}
      </header>
      <nav className=" hidden border-y text-sm font-medium md:flex ">
        {/* eslint-disable-next-line no-nested-ternary */}
        {pathname.includes('/checkout') ||
        pathname.includes('pre-approval') ? null : pathname.includes(
            'partner'
          ) ? (
          <PartnerHeader />
        ) : (
          <ul className="container flex items-center justify-center  gap-5 py-3 text-sm">
            {productStore.isLoading && (
              <li className="text-[#3C3C3C]">Loading categories...</li>
            )}
            {!productStore.isLoading &&
              menuItemList.map((item) => (
                <Link
                  href={`/category/${item.slug}`}
                  className="cursor-pointer text-[#3C3C3C] underline-offset-2 transition-all hover:font-bold hover:underline"
                  key={item.id}
                >
                  {item.name}
                </Link>
              ))}
          </ul>
        )}
      </nav>
    </div>
  );
});

Header.displayName = 'Header';

export default Header;
